import { useEffect, useRef, useState, memo } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { useCallback } from "react";
import { useConfig } from "../../hooks/useConfig";
import { GOOGLE_MAPS_LIBRARIES } from "@/constants/googleMaps";

/**
 * Map component that displays multiple routes with origin and destination markers,
 * as well as resource location markers for drivers/technicians with active shifts.
 * @param {Object} props
 * @param {Array} props.routes - Array of route objects containing origin and destination addresses
 * @param {Array} props.resources - Array of resources with currentLocation for displaying driver markers
 * @param {Object} props.center - Default center position {lat, lng}
 * @param {number} props.zoom - Default zoom level
 * @param {Function} [props.onEventClick] - Optional callback function when a route/event is clicked
 * @param {Function} [props.onResourceClick] - Optional callback function when a resource marker is clicked
 * @returns {JSX.Element}
 */
export const Map = memo(
  ({
    routes = [],
    resources = [],
    center,
    zoom = 4,
    onEventClick = null,
    onResourceClick = null,
  }) => {
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const [directionsService, setDirectionsService] = useState(null);
    const [directionsRenderers, setDirectionsRenderers] = useState([]);
    const [resourceMarkers, setResourceMarkers] = useState([]);
    const [geocoder, setGeocoder] = useState(null);
    const { configs } = useConfig();

    // Use refs for callbacks to avoid re-running effects when callbacks change
    const onEventClickRef = useRef(onEventClick);
    const onResourceClickRef = useRef(onResourceClick);

    // Keep refs updated
    useEffect(() => {
      onEventClickRef.current = onEventClick;
    }, [onEventClick]);

    useEffect(() => {
      onResourceClickRef.current = onResourceClick;
    }, [onResourceClick]);
    // Get API key from config (backend) with fallback to env variable for local development
    const apiKey =
      configs?.googleMapsApiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    // Initialize Google Maps (only once)
    useEffect(() => {
      if (!mapRef.current || map || !apiKey) return; // Don't re-initialize if map already exists or no API key

      const initializeMap = () => {
        if (!mapRef.current || !window.google?.maps) return;

        const googleMap = new window.google.maps.Map(mapRef.current, {
          center,
          zoom,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        });

        setMap(googleMap);
        setDirectionsService(new window.google.maps.DirectionsService());
        setGeocoder(new window.google.maps.Geocoder());
      };

      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        // Google Maps is already loaded, just initialize the map
        initializeMap();
      } else {
        // Google Maps is not loaded, use the loader
        const loader = new Loader({
          apiKey: apiKey,
          version: "weekly",
          libraries: GOOGLE_MAPS_LIBRARIES,
        });

        loader
          .load()
          .then(() => {
            initializeMap();
          })
          .catch((error) => {
            console.error("Error loading Google Maps:", error);
          });
      }

      return () => {
        // Cleanup renderers when component unmounts
        directionsRenderers.forEach((renderer) => renderer.setMap(null));
      };
    }, [apiKey]); // Re-run if API key changes

    // Update map center and zoom when props change
    useEffect(() => {
      if (map && center) {
        map.setCenter(center);
      }
    }, [map, center]);

    useEffect(() => {
      if (map && zoom) {
        map.setZoom(zoom);
      }
    }, [map, zoom]);

    // Geocode address to coordinates
    const geocodeAddress = useCallback(
      (address) => {
        return new Promise((resolve, reject) => {
          if (!geocoder) return reject("Geocoder not initialized");

          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK") {
              resolve(results[0].geometry.location);
            } else {
              reject(`Geocoding failed: ${status}`);
            }
          });
        });
      },
      [geocoder]
    );

    // Generate avatar marker icon
    const createAvatarMarkerIcon = useCallback(
      (avatarUrl, name, color, size = 40) => {
        return new Promise((resolve) => {
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");

          // Draw white border circle
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2 - 2, 0, 2 * Math.PI);
          ctx.fillStyle = "#FFFFFF";
          ctx.fill();
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.stroke();

          // Draw avatar or initials
          if (avatarUrl) {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              // Draw image in circle
              ctx.save();
              ctx.beginPath();
              ctx.arc(size / 2, size / 2, size / 2 - 4, 0, 2 * Math.PI);
              ctx.clip();
              ctx.drawImage(img, 2, 2, size - 4, size - 4);
              ctx.restore();
              resolve(canvas.toDataURL());
            };
            img.onerror = () => {
              // Fallback to initials if image fails
              drawInitials(ctx, name, color, size);
              resolve(canvas.toDataURL());
            };
            img.src = avatarUrl;
          } else {
            // Draw initials
            drawInitials(ctx, name, color, size);
            resolve(canvas.toDataURL());
          }
        });
      },
      []
    );

    // Helper to draw initials
    const drawInitials = (ctx, name, color, size) => {
      // Get initials
      const initials = name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

      // Draw background circle with route color
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 4, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      // Draw initials
      ctx.fillStyle = "#FFFFFF";
      ctx.font = `bold ${Math.floor(size * 0.35)}px Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initials, size / 2, size / 2);
    };

    // Calculate and display routes
    useEffect(() => {
      if (!map || !directionsService || !geocoder || routes.length === 0)
        return;

      // Clear existing renderers
      directionsRenderers.forEach((renderer) => renderer.setMap(null));
      const newRenderers = [];

      // Process each route
      const processRoutes = async () => {
        // Modern, professional color palette
        const colors = [
          "#3B82F6", // Blue
          "#10B981", // Green
          "#F59E0B", // Amber
          "#EF4444", // Red
          "#8B5CF6", // Purple
          "#EC4899", // Pink
          "#06B6D4", // Cyan
          "#F97316", // Orange
          "#14B8A6", // Teal
          "#6366F1", // Indigo
        ];

        for (let i = 0; i < routes.length; i++) {
          const route = routes[i];
          const color = colors[i % colors.length];

          try {
            const renderer = new window.google.maps.DirectionsRenderer({
              map,
              suppressMarkers: true, // Suppress default markers, we'll use custom avatar markers
              polylineOptions: {
                strokeColor: color,
                strokeWeight: 5,
                strokeOpacity: 0.8,
              },
            });

            const request = {
              origin: route.origin,
              destination: route.destination,
              travelMode: "DRIVING",
            };

            // Calculate route
            const result = await new Promise((resolve, reject) => {
              directionsService.route(request, (result, status) => {
                if (status === "OK") {
                  resolve(result);
                } else {
                  reject(status);
                }
              });
            });

            renderer.setDirections(result);
            newRenderers.push(renderer);

            // Create subtle avatar markers at origin and destination
            const originMarker = result.routes[0].legs[0]?.start_location;
            const destMarker =
              result.routes[0].legs[result.routes[0].legs.length - 1]
                ?.end_location;

            // Helper to create hover info window content for route markers
            const createRouteInfoContent = () => {
              const content = document.createElement("div");
              content.style.cssText = `
              padding: 12px 16px;
              min-width: 180px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              cursor: pointer;
              border-radius: 8px;
            `;

              // Calculate ETA display
              const etaDisplay = route.estimatedDuration
                ? `<div style="
                  font-size: 12px;
                  color: #10B981;
                  margin-top: 4px;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                ">
                  <span>ETA:</span>
                  <span style="font-weight: 500;">${Math.round(
                    route.estimatedDuration / 60
                  )} min</span>
                </div>`
                : "";

              content.innerHTML = `
              <div style="
                display: flex;
                align-items: center;
                gap: 10px;
                margin-bottom: 6px;
              ">
                <div style="
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background-color: ${color};
                  flex-shrink: 0;
                  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
                "></div>
                <div style="
                  font-weight: 600;
                  font-size: 14px;
                  color: #1F2937;
                  line-height: 1.4;
                ">${route.driverName}</div>
              </div>
              ${
                route.eventTitle
                  ? `
                <div style="
                  font-size: 12px;
                  color: #6B7280;
                  margin-top: 2px;
                  line-height: 1.4;
                ">${route.eventTitle}</div>
              `
                  : ""
              }
              ${etaDisplay}
              <div style="
                font-size: 11px;
                color: #9CA3AF;
                margin-top: 8px;
                padding-top: 8px;
                border-top: 1px solid #E5E7EB;
              ">Click to view details</div>
            `;

              // Add click handler
              if (onEventClickRef.current && route.eventId) {
                content.addEventListener("click", () => {
                  onEventClickRef.current?.(route.eventId);
                });
                content.addEventListener("mouseenter", () => {
                  content.style.backgroundColor = "#F9FAFB";
                });
                content.addEventListener("mouseleave", () => {
                  content.style.backgroundColor = "transparent";
                });
              }

              return content;
            };

            // Create avatar marker icon
            createAvatarMarkerIcon(
              route.driverAvatar,
              route.driverName,
              color,
              36 // Small, subtle size
            ).then((iconUrl) => {
              // Create origin marker with hover info window
              if (originMarker) {
                const marker = new window.google.maps.Marker({
                  position: originMarker,
                  map: map,
                  icon: {
                    url: iconUrl,
                    scaledSize: new window.google.maps.Size(36, 36),
                    anchor: new window.google.maps.Point(18, 18),
                  },
                  title: `${route.driverName} - Click to view route`,
                  zIndex: 1000 + i,
                });

                // Create info window for hover
                const infoWindow = new window.google.maps.InfoWindow({
                  content: createRouteInfoContent(),
                });

                // Show on hover
                marker.addListener("mouseover", () => {
                  infoWindow.open(map, marker);
                });

                marker.addListener("mouseout", () => {
                  infoWindow.close();
                });

                // Click handler
                if (onEventClickRef.current && route.eventId) {
                  marker.addListener("click", () => {
                    onEventClickRef.current?.(route.eventId);
                  });
                }
              }

              // Create destination marker with hover info window (only if different from origin)
              if (destMarker && destMarker !== originMarker) {
                const marker = new window.google.maps.Marker({
                  position: destMarker,
                  map: map,
                  icon: {
                    url: iconUrl,
                    scaledSize: new window.google.maps.Size(36, 36),
                    anchor: new window.google.maps.Point(18, 18),
                  },
                  title: `${route.driverName} - Click to view route`,
                  zIndex: 1000 + i,
                });

                // Create info window for hover
                const infoWindow = new window.google.maps.InfoWindow({
                  content: createRouteInfoContent(),
                });

                // Show on hover
                marker.addListener("mouseover", () => {
                  infoWindow.open(map, marker);
                });

                marker.addListener("mouseout", () => {
                  infoWindow.close();
                });

                // Click handler
                if (onEventClickRef.current && route.eventId) {
                  marker.addListener("click", () => {
                    onEventClickRef.current?.(route.eventId);
                  });
                }
              }
            });
          } catch (error) {
            console.error(`Error processing route: ${error}`);
          }
        }
      };

      processRoutes();
      setDirectionsRenderers(newRenderers);
    }, [map, directionsService, geocoder, routes, createAvatarMarkerIcon]);

    // Render resource location markers (for drivers/technicians with active shifts)
    useEffect(() => {
      if (!map) return;

      // Clear existing resource markers
      resourceMarkers.forEach((marker) => marker.setMap(null));
      const newResourceMarkers = [];

      // Filter resources that have current location data
      const resourcesWithLocation = resources.filter(
        (resource) =>
          resource.currentLocation?.lat && resource.currentLocation?.lng
      );

      if (resourcesWithLocation.length === 0) {
        setResourceMarkers([]);
        return;
      }

      // Color for "on shift" markers (different from route colors to distinguish)
      const shiftMarkerColor = "#6B7280"; // Gray for on-shift but not on active route

      const processResourceMarkers = async () => {
        for (let i = 0; i < resourcesWithLocation.length; i++) {
          const resource = resourcesWithLocation[i];
          const { lat, lng } = resource.currentLocation;

          try {
            // Create avatar marker icon
            const iconUrl = await createAvatarMarkerIcon(
              resource.avatar || resource.profilePicture || resource.image,
              resource.name || resource.fullName || "Unknown",
              shiftMarkerColor,
              40 // Slightly larger for visibility
            );

            const marker = new window.google.maps.Marker({
              position: { lat, lng },
              map: map,
              icon: {
                url: iconUrl,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              },
              title: `${resource.name || resource.fullName} - On Shift`,
              zIndex: 500 + i, // Below route markers but visible
            });

            // Add info window on hover
            const infoWindowContent = document.createElement("div");
            infoWindowContent.style.cssText = `
            padding: 12px 16px;
            min-width: 180px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            cursor: pointer;
            border-radius: 8px;
          `;
            infoWindowContent.innerHTML = `
            <div style="
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 8px;
            ">
              <div style="
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background-color: #10B981;
                flex-shrink: 0;
                box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
              "></div>
              <div style="
                font-weight: 600;
                font-size: 14px;
                color: #1F2937;
                line-height: 1.4;
              ">${resource.name || resource.fullName}</div>
            </div>
            <div style="
              font-size: 12px;
              color: #6B7280;
              margin-top: 4px;
              line-height: 1.4;
            ">On Shift${
              resource.currentLocation?.updatedAt
                ? ` • Updated ${new Date(
                    resource.currentLocation.updatedAt
                  ).toLocaleTimeString()}`
                : ""
            }</div>
            ${
              resource.phone
                ? `<div style="
                font-size: 12px;
                color: #6B7280;
                margin-top: 2px;
              ">${resource.phone}</div>`
                : ""
            }
            <div style="
              font-size: 11px;
              color: #9CA3AF;
              margin-top: 6px;
              padding-top: 6px;
              border-top: 1px solid #E5E7EB;
            ">Click to view details</div>
          `;

            const infoWindow = new window.google.maps.InfoWindow({
              content: infoWindowContent,
            });

            // Show info window on hover
            marker.addListener("mouseover", () => {
              infoWindow.open(map, marker);
            });

            marker.addListener("mouseout", () => {
              infoWindow.close();
            });

            // Handle click
            if (onResourceClickRef.current) {
              marker.addListener("click", () => {
                onResourceClickRef.current?.(resource);
              });
              infoWindowContent.addEventListener("click", () => {
                onResourceClickRef.current?.(resource);
              });
            }

            newResourceMarkers.push(marker);
          } catch (error) {
            console.error(`Error creating resource marker: ${error}`);
          }
        }
      };

      processResourceMarkers();
      setResourceMarkers(newResourceMarkers);

      // Cleanup function
      return () => {
        newResourceMarkers.forEach((marker) => marker.setMap(null));
      };
    }, [map, resources, createAvatarMarkerIcon]);

    return (
      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      />
    );
  }
);

Map.displayName = "Map";

export default Map;
